import { useEffect, useRef, useState } from "react";
import type { ChatMessage } from "../types";
import CardRenderer from "./cards/CardRenderer";
import AudioPlayer from "./AudioPlayer";
import ErrorBoundary from "./ErrorBoundary";
import FeedbackButtons from "./FeedbackButtons";
import { useSessionStore } from "../store/sessionStore";
import FreshnessLine from "./FreshnessLine";

// Best-effort map from visual card_type → router intent so the admin
// dashboard's "by intent" breakdown is populated.  Ambiguous (e.g.
// type_card could be type_info OR raid_counter) — we pick the more
// common case.
const CARD_TYPE_TO_INTENT: Record<string, string> = {
  pokemon_card: "base_stats",
  comparison_card: "comparison",
  ranking_card: "league_ranking",
  top_n_card: "meta_question",
  iv_card: "iv_ranking",
  best_ivs_card: "best_ivs",
  moveset_card: "move_list",
  evolution_card: "evolution_info",
  type_card: "type_info",
  candy_cost_card: "candy_cost",
  lore_card: "lore_general",
  xl_candy_card: "xl_candy",
  elite_tm_card: "elite_tm_info",
  move_detail_card: "move_detail",
  move_comparison_card: "move_comparison",
  meta_card: "meta_question",
  compound_card: "general",
};

interface ChatThreadProps {
  messages: ChatMessage[];
  onExampleClick: (q: string) => void;
  /** True when the most recent narration attempt was silently dropped by
   *  the browser (typically iOS Safari blocking autoplay).  When true,
   *  show a prominent tap-to-play fallback on the latest assistant
   *  message so the user can recover with one tap. */
  autoplayBlocked?: boolean;
  /** The message Scout's voice is reading now, or null; its button shows Stop. */
  speakingId?: string | null;
  /** Play a message's answer aloud (its Play button, or the blocked-autoplay
   *  recovery).  The handler clears autoplayBlocked. */
  onPlay?: (id: string, text: string) => void;
  /** Stop Scout's voice completely. */
  onStop?: () => void;
}

const EXAMPLES = [
  "What are Garchomp's base stats?",
  "Best Great League Pokémon?",
  "Is 15/14/15 a good IV for Umbreon in Great League?",
  "What moves should my Lucario have?",
  "How does Eevee evolve?",
  "Tell me about Mewtwo lore",
];

export default function ChatThread({
  messages,
  onExampleClick,
  autoplayBlocked = false,
  speakingId = null,
  onPlay,
  onStop,
}: ChatThreadProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  // Tracks the last completed assistant message so we can announce it once.
  const lastAnnouncedRef = useRef<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const sessionId = useSessionStore((s) => s.sessionId);
  // Tracks message count so the scroll-into-view effect only fires when a NEW
  // turn appears, not on every token append during streaming.  Streaming
  // updates the same message in place; scrolling on every token caused a
  // visible reflow / "screen shift" especially on iPhone.
  const prevMessageCountRef = useRef(messages.length);

  // Find the most recent fully-streamed assistant message — this is what
  // the tap-to-play recovery affordance plays back when autoplay is blocked.
  const latestAssistantWithContent = [...messages].reverse().find(
    (m) => m.role === "assistant" && !m.streaming && !!m.content,
  );

  useEffect(() => {
    // Only scroll when the number of messages grows (new question or new
    // assistant reply added).  Use instant ("auto") scrolling so the viewport
    // doesn't visibly animate — important for stable demo recordings and
    // matches how most modern chat apps (Claude, ChatGPT) behave.
    if (messages.length > prevMessageCountRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: "auto" });
      prevMessageCountRef.current = messages.length;
    }
  }, [messages.length]);

  // Announce to screen readers when a new assistant answer finishes streaming.
  useEffect(() => {
    const lastAssistant = [...messages].reverse().find(
      (m) => m.role === "assistant" && !m.streaming && m.content,
    );
    if (lastAssistant && lastAssistant.id !== lastAnnouncedRef.current) {
      lastAnnouncedRef.current = lastAssistant.id;
      setAnnouncement("Answer ready");
      // Clear after a tick so the same text can re-trigger on the next answer.
      setTimeout(() => setAnnouncement(""), 1000);
    }
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto flex flex-col items-center justify-center px-4 py-4 sm:py-12 gap-4 sm:gap-8 animate-fade-in min-h-0">
        {/* Hero */}
        <div className="text-center space-y-1 max-w-sm">
          <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-scout-red/10 border border-scout-red/20 flex items-center justify-center mx-auto mb-2 sm:mb-4">
            <svg viewBox="0 0 24 24" className="w-6 h-6 sm:w-8 sm:h-8 fill-scout-red" aria-hidden="true">
              <path d="M12 2a10 10 0 0 1 9.95 9H14.9a3 3 0 0 0-5.8 0H2.05A10 10 0 0 1 12 2zm0 20a10 10 0 0 1-9.95-9h7.05a3 3 0 0 0 5.8 0h7.05A10 10 0 0 1 12 22z" />
            </svg>
          </div>
          <h1 className="text-lg sm:text-xl font-bold text-white">Ask Scout anything</h1>
          <p className="text-sm text-white/40 leading-relaxed">
            Stats, IVs, rankings, movesets — just type your question.
          </p>
          <FreshnessLine />
        </div>

        {/* Example chips */}
        <div className="flex flex-col gap-1.5 w-full max-w-md">
          <p className="text-xs text-white/30 text-center uppercase tracking-widest mb-0.5">Try asking</p>
          {EXAMPLES.map((q) => (
            <button
              key={q}
              onClick={() => onExampleClick(q)}
              className="glass rounded-xl px-4 py-2 text-sm text-left text-white/70 hover:text-white hover:bg-white/[0.07] transition-all group"
            >
              <span className="text-scout-yellow mr-2 group-hover:translate-x-0.5 inline-block transition-transform">→</span>
              {q}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-6">
      {/* Hidden live region — announces "Answer ready" to screen readers when
          a new assistant response finishes streaming. Visually invisible. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </div>

      <div className="max-w-chat mx-auto space-y-6">
        {messages.map((msg, idx) => (
          <div
            key={msg.id}
            role={msg.role === "user" ? "article" : "article"}
            aria-label={msg.role === "user" ? "Your question" : "Scout's answer"}
            className="flex flex-col gap-2 animate-slide-up"
          >
            {/* Row: avatar + text bubble */}
            <div className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              {/* Avatar — assistant only */}
              {msg.role === "assistant" && (
                <div aria-hidden="true" className="flex-shrink-0 w-7 h-7 mt-0.5 rounded-full bg-scout-red/15 border border-scout-red/25 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-scout-red" aria-hidden="true">
                    <path d="M12 2a10 10 0 0 1 9.95 9H14.9a3 3 0 0 0-5.8 0H2.05A10 10 0 0 1 12 2zm0 20a10 10 0 0 1-9.95-9h7.05a3 3 0 0 0 5.8 0h7.05A10 10 0 0 1 12 22z" />
                  </svg>
                </div>
              )}

              <div className={`flex flex-col gap-2 max-w-[85%] sm:max-w-xl ${msg.role === "user" ? "items-end" : "items-start"}`}>
                {/* Text bubble — Play button lives inside so it's visually tied to the narration */}
                {msg.content || msg.streaming ? (
                  <div
                    {...(msg.role === "assistant" ? { "data-testid": "assistant-message" } : {})}
                    className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      msg.role === "user"
                        ? "bg-scout-blue text-white rounded-br-md"
                        : "bg-scout-elevated text-white/90 rounded-bl-md border border-white/[0.06]"
                    }`}
                  >
                    {msg.content || (
                      msg.streaming && (
                        <span className="text-white/40 italic">Generating answer…</span>
                      )
                    )}
                    {msg.streaming && (
                      <span className="inline-block w-0.5 h-3.5 ml-0.5 bg-current animate-blink align-middle" />
                    )}
                    {/* Play button + thumbs-up/down — shown once streaming is done */}
                    {msg.role === "assistant" && !msg.streaming && msg.content && (
                      <div className="-mx-1 mt-1.5 pt-1.5 border-t border-white/[0.06]">
                        {/* Recovery affordance — only on the latest assistant
                            message and only when autoplay was blocked by iOS.
                            The user taps once, audio plays, and the pill
                            dismisses for the rest of the session. */}
                        {autoplayBlocked && latestAssistantWithContent?.id === msg.id && (
                          <button
                            type="button"
                            onClick={() => onPlay?.(msg.id, msg.content)}
                            className="mb-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-scout-blue/15 hover:bg-scout-blue/25 text-scout-blue text-xs font-medium transition-colors animate-pulse-slow"
                          >
                            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
                              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4z" />
                            </svg>
                            Tap to hear response
                          </button>
                        )}
                        <AudioPlayer
                          playing={speakingId === msg.id}
                          onPlay={() => onPlay?.(msg.id, msg.content)}
                          onStop={() => onStop?.()}
                        />
                        {sessionId && (
                          <FeedbackButtons
                            sessionId={sessionId}
                            messageId={msg.id}
                            question={idx > 0 && messages[idx - 1].role === "user" ? messages[idx - 1].content : ""}
                            responseText={msg.content}
                            intent={msg.visual ? CARD_TYPE_TO_INTENT[msg.visual.card_type] : undefined}
                            cardType={msg.visual?.card_type}
                          />
                        )}
                      </div>
                    )}
                  </div>
                ) : msg.role === "assistant" && !msg.streaming ? (
                  /* Defensive fallback — backend should always send text, but just in case */
                  <div
                    data-testid="assistant-message"
                    className="px-4 py-2.5 rounded-2xl text-sm leading-relaxed bg-scout-elevated text-white/40 rounded-bl-md border border-white/[0.06] italic"
                  >
                    No answer found for that question.
                  </div>
                ) : null}
              </div>
            </div>

            {/* Visual card — full-width, outside the bubble column so width is never constrained */}
            {msg.role === "assistant" && msg.visual && msg.visual.card_type !== "empty_card" && (
              <ErrorBoundary>
                <CardRenderer payload={msg.visual} />
              </ErrorBoundary>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

